// SidePanelPage.tsx  ← archivo completo
import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  memo,
  type MouseEvent as RMouseEvent,
} from 'react';
import { useTranslation } from 'react-i18next';

import './SidePanelPage.css';

import { BinTablePanel } from './BinTablePanel';
import { CorrelationPanel } from './CorrelationPanel';
import { LineChartPanel } from './LineChartPanel';
import { ManeuversPanel } from './ManeuversPanel';
import { LineupPanel } from './LineupPanel';
import MapPanel, { type MapPanelProperties } from './MapPanel';
import { PolarPanel } from './PolarPanel';
import { PerformanceBucketsPanel } from './PerformanceBucketsPanel';
import { ScatterPanel } from './ScatterPanel';
import {
  ensureManeuverVisibilityForTacks,
  getManeuverVisibility,
  setManeuverVisibility,
  subscribeManeuverVisibility,
} from '../../state/maneuverVisibilityStore';
import { getCurrentColorScheme, subscribeToColorScheme } from '../../theme';
import { ReactComponent as SailTrackLogo } from '../../react-icons/SailTrackLogo.svg';
import { ReactComponent as SideBarSVG } from '../../react-icons/sidebar-left-svgrepo-com.svg';
import { type TrackPoint, type HighlightSegment, type Tack, type LayoutKey } from '../../types';
import {
  getCurrentMapColorMode,
  resolveMapColorScheme,
  subscribeToMapColorMode,
} from '../../utils/mapboxStyle';

import type mapboxgl from 'mapbox-gl';

export interface SidePanelPageProperties extends MapPanelProperties {
  isVisible: boolean;
  onClose: () => void;
  trackData: TrackPoint[];
  highlightSegments: HighlightSegment[];
  saveHighlightAsTraining: (segment: HighlightSegment) => void;
  addHighlight: (startIndex: number, endIndex?: number) => void;
  generateRandomColor: () => string;
  updateHighlight: (index: number, startIndex: number, endIndex: number, color: string) => void;
  draftHighlight: (HighlightSegment & { source: 'map' | 'chart' | 'chartPreview' }) | null;
  setDraftHighlight: React.Dispatch<
    React.SetStateAction<(HighlightSegment & { source: 'map' | 'chart' | 'chartPreview' }) | null>
  >;
  tacks: Tack[];
  /** Polars data for polar plot */
  polars: {
    angles: number[];
    tws: number[];
    speeds: number[][];
    stds: number[][];
    raw: string | null;
  };
  mode:
    | 'map'
    | 'line'
    | 'polar'
    | 'correlation'
    | 'scatter'
    | 'binTable'
    | 'maneuvers'
    | 'lineup'
    | 'performanceBuckets';
  primaryBoatName?: string | null;
  mapInstance1: React.MutableRefObject<mapboxgl.Map | null>;
  mapInstance2: React.MutableRefObject<mapboxgl.Map | null>;
  mapInstance3: React.MutableRefObject<mapboxgl.Map | null>;
  mapInstance4: React.MutableRefObject<mapboxgl.Map | null>;
  isTrackLoading: boolean;
  hidden?: boolean;
  layout: LayoutKey;
}

/*———————————————————  Constantes  ———————————————————*/
type ResizeDirection = 'e' | 'w' | 'n' | 's';
const MIN_PERCENT = 5;
const MAX_PERCENT = 95;
const MIN_PX = 100;

const clamp = (value: number) => Math.max(MIN_PERCENT, Math.min(MAX_PERCENT, value));

const getResolvedMapColorScheme = () =>
  resolveMapColorScheme(getCurrentColorScheme(), getCurrentMapColorMode());

const safeResizeMap = (map: mapboxgl.Map | null | undefined) => {
  if (!map) {
    return;
  }
  const container = map.getContainer?.() as HTMLElement | undefined;
  if (container && (container.clientWidth === 0 || container.clientHeight === 0)) {
    return;
  }

  // Narrow type for internal style checks without using `any`
  const style = (map as unknown as { style?: { applyProjectionUpdate?: () => void } }).style;
  if (!style || typeof style.applyProjectionUpdate !== 'function') {
    return;
  }

  if (!map.getStyle()) {
    return;
  }

  try {
    map.resize();
  } catch {
    /* silent – never expose internal errors */
  }
};

/*———————————————————  Componente  ———————————————————*/
const SidePanelPage: React.FC<SidePanelPageProperties> = (properties) => {
  const { t } = useTranslation('common');
  /* Destructuring largo para mantener legibilidad */
  const {
    trackData,
    relatedTrackLayers,
    highlightSegments,
    saveHighlightAsTraining,
    addHighlight,
    generateRandomColor,
    tacks,
    loaderKey,
    setSelectedHighlightSegment,
    setShowSaveTrainingModal,
    metricBounds,
    colorSchemes,
    races,
    startLineSegments,
    RenderizarRaces,
    cachedTrackData,
    selectedFile,
    primarySelectionType,
    canManageSelectedFile,
    showSidePage,
    setShowSidePage,
    showSidebar,
    setShowSidebar,
    globalToRelativeIndexMap,
    cachedTacksGeoJSON,
    marks,
    editingSegmentId,
    editingRange,
    editingMode,
    onEditingMarksChange,
    onEditingRangeChange,
    onSaveEditing,
    onCancelEditing,
    editingSegmentType,
    editingSegmentName,
    setEditingMode,
    loa,
    setHighlightSegments,
    setCachedTacksGeoJSON,
    combineSegments,
    getTackColor,
    lastFileId,
    setLoaderKey,
    removeHighlight,
    updateHighlight,
    highlightTrackSegment,
    draftHighlight,
    setDraftHighlight,
    polars,
    mode,
    mapInstance1,
    mapInstance2,
    mapInstance3,
    mapInstance4,
    mapInstance,
    mapContainer,
    sharedCameraRef,
    side,
    layout,
    panelId,
    hidden = false,
    primaryBoatName,
    isTrackLoading,
  } = properties;

  /*————————————————— ① Estado & refs —————————————————*/
  const [resizeDirection, setResizeDirection] = useState<ResizeDirection | null>(null);
  const [mapColorScheme, setMapColorScheme] = useState(getResolvedMapColorScheme);
  const lastPos = useRef({ x: 0, y: 0 });

  const panelReference = useRef<HTMLDivElement>(null);

  const rafResizeMaps = useRef<number | null>(null);
  /** ① redimensiona los 4 mapas de forma segura */
  const resizeAllMaps = () => {
    for (const r of [mapInstance1, mapInstance2, mapInstance3, mapInstance4]) {
      try {
        safeResizeMap(r.current);
      } catch {
        /* noop: nunca exponer detalles internos */
      }
    }
  };

  /** ② Throttle: garantiza un único resize por frame */
  const throttleFrame = (() => {
    let raf: number | null = null;
    return (callback: () => void) => {
      if (raf !== null) {
        return;
      }
      raf = requestAnimationFrame(() => {
        raf = null;
        callback();
      });
    };
  })();

  const scheduleResize = () => throttleFrame(resizeAllMaps);
  /* ──────────── ② NUEVO helper: agenda un único resize por frame ─── */
  const scheduleMapsResize = () => {
    if (rafResizeMaps.current !== null) {
      return;
    } // ya hay uno pendiente
    rafResizeMaps.current = requestAnimationFrame(() => {
      rafResizeMaps.current = null;
      for (const r of [mapInstance1, mapInstance2, mapInstance3, mapInstance4]) {
        try {
          safeResizeMap(r.current);
        } catch (error) {
          /* silencioso: nunca mostramos detalles internos al usuario */
          console.error('[scheduleMapsResize] resize:', error);
        }
      }
    });
  };
  /** Ajusta el % de la columna SIN dejar que ninguna pista baje
   *  de MIN_PX píxeles ni supere los MIN/MAX_PERCENT.               */
  /*—————————————————————————————  Helpers —————————————————————————————*/

  const [maneuverVisibility, setManeuverVisibilityState] = useState<Record<number, boolean>>({});

  useEffect(() => {
    setManeuverVisibilityState(getManeuverVisibility());
    const unsubscribe = subscribeManeuverVisibility(setManeuverVisibilityState);
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const syncMapColorScheme = () => setMapColorScheme(getResolvedMapColorScheme());
    const unsubscribeColorScheme = subscribeToColorScheme(syncMapColorScheme);
    const unsubscribeMapColorMode = subscribeToMapColorMode(syncMapColorScheme);
    syncMapColorScheme();
    return () => {
      unsubscribeColorScheme();
      unsubscribeMapColorMode();
    };
  }, []);

  useEffect(() => {
    ensureManeuverVisibilityForTacks(tacks);
  }, [tacks]);

  const handleManeuverVisibilityChange = useCallback((tackIndex: number, visible: boolean) => {
    setManeuverVisibility((previous) => ({
      ...previous,
      [tackIndex]: visible,
    }));
  }, []);

  /*———————————————— ① Helpers de redimensionado horizontal ————————————*/

  /** Ajusta el ancho cuando el panel es hijo directo de .main-container */
  const applyWidthMain = (clientX: number, direction: ResizeDirection) => {
    const panel = panelReference.current;
    if (!panel || !panel.parentElement) {
      return;
    }

    const main = panel.parentElement; // .main-container
    const rect = main.getBoundingClientRect();

    let pct =
      direction === 'e'
        ? ((clientX - rect.left) / rect.width) * 100
        : ((rect.right - clientX) / rect.width) * 100;
    pct = clamp(pct);

    const isLeft = ['l', 'tl', 'bl'].includes(side);
    const left = isLeft ? pct : 100 - pct;

    // Las unidades fr reparten sólo el espacio disponible después del gutter.
    main.style.gridTemplateColumns = `${left}fr ${100 - left}fr`;
    scheduleResize();
  };

  /** Ajusta el ancho dentro de una fila flex (cuadrantes)            */
  const applyWidthQuadrant = (clientX: number) => {
    const panel = panelReference.current;
    if (!panel || !panel.parentElement) {
      return;
    }

    const row = panel.parentElement; // .quadrants-row
    const rect = row.getBoundingClientRect();

    const panels = [...row.children].filter(
      (child): child is HTMLElement =>
        child instanceof HTMLElement && child.classList.contains('side-panel'),
    );
    if (panels.length !== 2) {
      return;
    }

    const gap = Number.parseFloat(getComputedStyle(row).columnGap) || 0;
    const availableWidth = Math.max(1, rect.width - gap);
    const pointerWithinTracks = clientX - rect.left - gap / 2;
    const minimumPercent = Math.min(
      50,
      Math.max(MIN_PERCENT, (MIN_PX / availableWidth) * 100),
    );
    const pct = Math.max(
      minimumPercent,
      Math.min(100 - minimumPercent, (pointerWithinTracks / availableWidth) * 100),
    );

    panels[0].style.flex = `${pct} 1 0px`;
    panels[1].style.flex = `${100 - pct} 1 0px`;
    scheduleResize();
  };

  /** Delegador: decide qué helper usar según el contenedor            */
  const applyWidth = (clientX: number, direction: ResizeDirection) => {
    const panel = panelReference.current;
    if (!panel || !panel.parentElement) {
      return;
    }

    if (panel.parentElement.classList.contains('main-container')) {
      applyWidthMain(clientX, direction);
    } else {
      applyWidthQuadrant(clientX);
    }
  };

  /** Ajusta la altura de los paneles con un divisor horizontal.
   *  – single: sin efecto (100 %)
   *  – quadrants:   fila 0 = pct fila 1 = 100-pct
   *  – cualquier otro layout (2 ó 3 paneles apilados):
   *                 fila superior = pct fila inferior = 100-pct           */
  const applyHeight = (clientY: number) => {
    const main = document.querySelector<HTMLDivElement>('.main-container');
    if (!main) {
      return;
    }

    const rect = main.getBoundingClientRect();

    /* Número de paneles activos */
    const totalPanels = document.querySelectorAll('.main-container .side-panel').length;

    /* Si sólo hay uno: ocupa todo el alto */
    if (totalPanels <= 1) {
      main.style.gridTemplateRows = '1fr';
      scheduleMapsResize();
      return;
    }

    const gap = Number.parseFloat(getComputedStyle(main).rowGap) || 0;
    const availableHeight = Math.max(1, rect.height - gap);
    const pointerWithinTracks = clientY - rect.top - gap / 2;
    const minimumPercent = Math.min(
      50,
      Math.max(MIN_PERCENT, (MIN_PX / availableHeight) * 100),
    );
    const pct = Math.max(
      minimumPercent,
      Math.min(100 - minimumPercent, (pointerWithinTracks / availableHeight) * 100),
    );

    /* ───────────────────── 1) Layout QUADRANTS ───────────────────── */
    if (main.classList.contains('quadrants')) {
      const rows = main.querySelectorAll<HTMLDivElement>('.quadrants-row');
      if (rows.length === 2) {
        main.style.gridTemplateRows = '';
        rows[0].style.flex = `${pct} 1 0px`;
        rows[1].style.flex = `${100 - pct} 1 0px`;
      } else {
        /* Fallback de seguridad */
        main.style.gridTemplateRows = `${pct}fr ${100 - pct}fr`;
      }
      scheduleMapsResize();
      return;
    }

    /* ───────────────────── 2) Todos los demás layouts ────────────── */
    /* (top-bottom, two-top-one-bottom, one-top-two-bottom,
	   two-left-one-right, one-left-two-right…)                      */
    main.style.gridTemplateRows = `${pct}fr ${100 - pct}fr`;
    scheduleMapsResize();
  };

  /*————————————————  Mouse-handlers ———————————————*/

  /** ③  Se ejecuta en cada movimiento mientras se arrastra el asa */
  /*————————————————— ③  Movimiento mientras arrastras —————————————————*/
  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (!resizeDirection) {
        return;
      }
      lastPos.current = { x: event.clientX, y: event.clientY };
      if (resizeDirection === 'e' || resizeDirection === 'w') {
        applyWidth(event.clientX, resizeDirection);
      } else {
        applyHeight(event.clientY);
      }
      resizeAllMaps();
    },
    [resizeDirection, applyWidth, applyHeight, resizeAllMaps],
  );

  /*————————————————— ④  Al soltar el botón del ratón —————————————————*/
  const stopResize = useCallback(
    (event: MouseEvent) => {
      if (!resizeDirection) {
        return;
      }
      if (resizeDirection === 'e' || resizeDirection === 'w') {
        applyWidth(event.clientX, resizeDirection);
      } else {
        applyHeight(event.clientY);
      }
      resizeAllMaps();
      setResizeDirection(null);
    },
    [resizeDirection, applyWidth, applyHeight, resizeAllMaps],
  );
  /*—————————————————— NUEVO helper: restablecer layout ——————————————————*/
  const resetAllPanels = () => {
    try {
      const main = document.querySelector<HTMLDivElement>('.main-container');
      if (!main) {
        return;
      }

      /* Limpia estilos de grid/flex para volver al CSS por defecto */
      main.style.gridTemplateColumns = '';
      main.style.gridTemplateRows = '';

      /* Quadrants & paneles: flex-basis vacío → tamaño original */
      for (const element of main.querySelectorAll<HTMLElement>('.quadrants-row, .side-panel')) {
        element.style.flex = '';
      }

      /* Fuerza el resize final de todos los mapas */
      resizeAllMaps();
    } catch {
      /* silent fail: nunca exponemos detalles internos */
    }
  };

  /*—————————————————— Atajo global Cmd/Ctrl + ⇧ + R ——————————————————*/
  useEffect(() => {
    const handleKeydown = (event: KeyboardEvent) => {
      const isResetCombo =
        event.shiftKey && (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'r';
      if (!isResetCombo) {
        return;
      }

      event.preventDefault();
      resetAllPanels();
    };

    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [resetAllPanels]);

  /* Attach / detach mouse listeners */
  useEffect(() => {
    if (resizeDirection) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', stopResize);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', stopResize);
    };
  }, [resizeDirection, handleMouseMove, stopResize]);

  /*————————————————— ⑦ Qué bordes actúan como resizer —————————————————*/
  /*————————————————— ⑦ Qué bordes actúan como resizer —————————————————*/
  const resizers: ResizeDirection[] = [];

  // El layout es una fuente estable; no dependemos de leer el DOM durante render.
  if (layout !== 'single') {
    if (['l', 'tl', 'bl'].includes(side)) {
      resizers.push('e');
    } // borde derecho
    if (['r', 'tr', 'br'].includes(side)) {
      resizers.push('w');
    } // borde izquierdo
  }

  if (layout !== 'single') {
    if (['t', 'tl', 'tr'].includes(side)) {
      resizers.push('s');
    }
    if (['b', 'bl', 'br'].includes(side)) {
      resizers.push('n');
    }
  }
  const handleToggleSidebar = () => setShowSidebar((previous) => !previous);
  const toggleSidebarLabel = showSidebar
    ? t('race.sidebar.hide', 'Hide sidebar')
    : t('race.sidebar.show', 'Show sidebar');

  /*————————————————— ⑧ Render —————————————————*/
  return (
    <div
      ref={panelReference}
      className={`side-panel${hidden ? ' hidden' : ''}`}
      data-side={side}
      style={{
        width: '100%',
        height: '100%',
        gridArea: side,
        position: 'relative',
      }}
    >
      {/* Resizers */}
      {resizers.map((direction) => (
        <button
          type="button"
          key={direction}
          className={`side-panel-resizer resizer-${direction}`}
          data-active={resizeDirection === direction}
          data-map-color-scheme={mapColorScheme}
          aria-label={`Resize ${direction}`}
          onMouseDown={(event: RMouseEvent) => {
            event.preventDefault();
            setResizeDirection(direction);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              setResizeDirection(direction);
            }
          }}
        />
      ))}

      {/* Contenido principal */}
      <div
        className="side-panel-content"
        aria-busy={isTrackLoading}
        style={{
          height: '100%',
          overflow:
            mode === 'polar' ||
            mode === 'correlation' ||
            mode === 'scatter' ||
            mode === 'binTable' ||
            mode === 'lineup' ||
            mode === 'maneuvers' ||
            mode === 'performanceBuckets'
              ? 'auto'
              : 'hidden',
        }}
      >
        {mode === 'map' && (
          <MapPanel
            {...{
              loaderKey,
              highlightSegments,
              setSelectedHighlightSegment,
              setShowSaveTrainingModal,
              metricBounds,
              colorSchemes,
              highlightTrackSegment,
              races,
              startLineSegments,
              RenderizarRaces,
              cachedTrackData,
              selectedFile,
              primarySelectionType,
              tacks,
              onManeuverVisibilityChange: handleManeuverVisibilityChange,
              showSidePage,
              setShowSidePage,
              showSidebar,
              setShowSidebar,
              globalToRelativeIndexMap,
              cachedTacksGeoJSON,
              marks,
              editingSegmentId,
              editingRange,
              editingMode,
              onEditingMarksChange,
              onEditingRangeChange,
              onSaveEditing,
              onCancelEditing,
              editingSegmentType,
              editingSegmentName,
              setEditingMode,
              loa,
              setHighlightSegments,
              setCachedTacksGeoJSON,
              combineSegments,
              getTackColor,
              lastFileId,
              setLoaderKey,
              trackData,
              relatedTrackLayers,
              maneuverVisibility,
              removeHighlight,
              generateRandomColor,
              mapInstance,
              mapContainer,
              side,
              panelId,
              setDraftHighlight,
              sharedCameraRef,
              canManageSelectedFile,
              polars,
              suppressLoadingOverlay: isTrackLoading,
            }}
          />
        )}
        {mode === 'line' && (
          <LineChartPanel
            {...{
              trackData,
              highlightSegments,
              tacks,
              saveHighlightAsTraining,
              canManageSelectedFile,
              addHighlight,
              generateRandomColor,
              updateHighlight,
              removeHighlight,
              side,
              draftHighlight,
              setDraftHighlight,
            }}
          />
        )}
        {mode === 'polar' && (
          <div
            style={{
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <PolarPanel
              {...{
                trackData,
                metricBounds,
                polars,
              }}
            />
          </div>
        )}
        {mode === 'scatter' && (
          <div
            style={{
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <ScatterPanel
              {...{
                trackData,
                tacks,
                relatedTrackLayers,
                primaryBoatName,
                highlightSegments,
              }}
            />
          </div>
        )}
        {mode === 'correlation' && (
          <div
            style={{
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <CorrelationPanel
              {...{
                trackData,
                metricBounds,
              }}
            />
          </div>
        )}
        {mode === 'lineup' && (
          <div
            style={{
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <LineupPanel
              {...{
                trackData,
                highlightSegments,
                relatedTrackLayers,
                tacks,
                primaryBoatName,
                globalToRelativeIndexMap,
              }}
            />
          </div>
        )}
        {mode === 'maneuvers' && (
          <div
            style={{
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <ManeuversPanel
              {...{
                trackData,
                tacks,
                globalToRelativeIndexMap,
                maneuverVisibility,
                onManeuverVisibilityChange: handleManeuverVisibilityChange,
              }}
            />
          </div>
        )}
        {mode === 'binTable' && (
          <div
            style={{
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              flex: '1 1 auto',
              minHeight: 0,
            }}
          >
            <BinTablePanel
              {...{
                trackData,
              }}
            />
          </div>
        )}
        {mode === 'performanceBuckets' && (
          <div
            style={{
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <PerformanceBucketsPanel trackData={trackData} />
          </div>
        )}
        {isTrackLoading && (
          <>
            <div className="map-loading-overlay" />
            <div className="loader" role="status" aria-live="polite">
              <div className="loader-content">
                <SailTrackLogo className="logo" aria-hidden="true" focusable="false" />
                <span className="text">Sailtrack</span>
              </div>
              <div className="loader-content-transparent">
                <SailTrackLogo className="logo" aria-hidden="true" focusable="false" />
                <span className="text">Sailtrack</span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Botón para ocultar/mostrar sidebar */}
      {['l', 'tl', 't'].includes(side) && (
        <button
          className={showSidebar ? 'hide-sidebar-button' : 'toggle-sidebar-button'}
          onClick={handleToggleSidebar}
          aria-label={toggleSidebarLabel}
          title={toggleSidebarLabel}
        >
          <SideBarSVG />
        </button>
      )}
    </div>
  );
};

// eslint-disable-next-line import/no-default-export
export default memo(SidePanelPage);
